"use client";

/**
 * Motion system foundation (build-prompt Phase 4) — numeral count-up.
 *
 * useCountUp(target) — returns a number that eases from its previous value
 * to `target` over 600ms (ease-out). Under prefers-reduced-motion the value
 * jumps to `target` instantly.
 *
 *   const value = useCountUp(8742);
 *   return <span className="font-tabular">{Math.round(value).toLocaleString()}</span>;
 *
 * Formatting is the caller's job — the hook returns the raw interpolated
 * number so it works for ints, currencies, and percentages alike. Pair
 * with `font-tabular` (tabular-nums) so digits don't jitter horizontally.
 *
 * Options:
 * - `start` (default true): pass false to hold at `from` until ready —
 *   e.g. gate on a reveal: `useCountUp(n, { start: revealed })`.
 * - `from` (default 0): initial value before the first animation.
 * - `durationMs` (default 600 = --wtm-dur-slow).
 */

import { useEffect, useRef, useState } from "react";
import { usePrefersReducedMotion } from "./reduced-motion";

export interface UseCountUpOptions {
  durationMs?: number;
  start?: boolean;
  from?: number;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

export function useCountUp(target: number, options: UseCountUpOptions = {}): number {
  const { durationMs = 600, start = true, from = 0 } = options;
  const reducedMotion = usePrefersReducedMotion();
  const [value, setValue] = useState(from);
  const valueRef = useRef(value);
  valueRef.current = value;
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!start) return;

    if (reducedMotion || typeof requestAnimationFrame === "undefined") {
      setValue(target); // instant completed state
      return;
    }

    const fromValue = valueRef.current;
    if (fromValue === target) return;

    let startTs: number | null = null;
    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const t = Math.min((ts - startTs) / durationMs, 1);
      const next = fromValue + (target - fromValue) * easeOutCubic(t);
      setValue(t >= 1 ? target : next);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
    // Re-run when the target changes (animates from the current shown value).
  }, [target, start, durationMs, reducedMotion]);

  return value;
}
